import React, { useEffect } from "react";

import Tab, { TabProps } from "@atoms/Tab";
import TabPanel from "@atoms/TabPanel";

import { atom, SetStateAction, useAtom, WritableAtom } from "@libraries/jotai";
import { uuidv4 } from "@libraries/uuid";

import * as Styled from "./styles";

export interface PageTabProps {
  id: string;
  key?: string;
  tab: TabProps;
  tabPanel: JSX.Element;
}

interface PageTabsProps {
  content: PageTabProps[];
  selectedTab: number;
  setContent: (update: SetStateAction<PageTabProps[]>) => void;
  setSelectedTab: (update: SetStateAction<number>) => void;
}

export const createNewTabsAtom = (initialTabs: PageTabProps[]) => atom<PageTabProps[]>(initialTabs);

export function useTabsAtom(
  tabsAtom: WritableAtom<PageTabProps[], SetStateAction<PageTabProps[]>, void>,
  selectedTabAtom: WritableAtom<number, SetStateAction<number>, void>
) {
  const [pageTabs, setPageTabs] = useAtom(tabsAtom);
  const [selectedTab, setSelectedTab] = useAtom(selectedTabAtom);

  /**
   * add a new tab if it doesn't already exist
   * @param newTab new tab to be added
   * @returns index of new tab added or existing tab
   */
  const addNewTab = (newTab: PageTabProps): number => {
    const indexNewTab = pageTabs.findIndex((pageTab) => pageTab.id === newTab.id);

    const tabAlreadyExists = indexNewTab > 0;

    if (tabAlreadyExists) {
      return indexNewTab;
    }

    setPageTabs((value) => [...value, newTab]);

    return pageTabs.length;
  };

  return { pageTabs, setPageTabs, addNewTab, selectedTab, setSelectedTab };
}

const PageTabs: React.FC<PageTabsProps> = ({ content, selectedTab, setContent, setSelectedTab }) => {

  const handleChange = (_: any, newValue: number) => {
    setSelectedTab(newValue);
  };

  const onCloseTab = (tabIndex: any) => {

    const updatedTabs = content.filter((_, index) => index !== tabIndex);

    if (tabIndex <= selectedTab) {
      setSelectedTab(selectedTab - 1);
    }

    setContent(updatedTabs);
  };

  const contentLength = content.length;

  /** generate key for each tab */
  useEffect(() => {
    setContent((state) => {
      const newState = [...state];

      newState.forEach((item, i) => {
        if (item.key) return;

        newState[i].key = uuidv4();
      });

      return newState;
    });
  }, [contentLength]);

  return (
    <>
      <Styled.PageTabsContainer
        value={selectedTab}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons
        allowScrollButtonsMobile
        aria-label="scrollable force tabs"
      >
        {content.map(({ tab, key }, index) => (
          <Tab
            key={`tab_${key}`}
            tab={tab}
            onClose={() => onCloseTab(index)}
          />
        ))}
      </Styled.PageTabsContainer>
      {content.map(({ tabPanel, key }, index) => (
        <TabPanel key={`tabPanel_${key}`} id="full-width-supplypanel" value={selectedTab} index={index}>
          {tabPanel}
        </TabPanel>
      ))}
    </>
  );
};

export default PageTabs;
